import { action, computed, flow, makeObservable, observable, toJS } from 'mobx';
import api from '../utils/apiService';
import { makePersistable } from 'mobx-persist-store';

import { apiFetcher } from '../utils/fetch';
import { updateItems } from './utils';

const defaultFilter = {
  search: '',
  roomOccupancy: ["vacant", "occupied", "booked", "available_soon"],
  roomStatus: ["clean", "dirty", "out_of_order"],
  property: "",
  beginDate: "",
  page: 1,
  limit: 20,
}
export default class Room {
  constructor() {
    this.selected = {};

    this.filters = defaultFilter;

    this.items = [];
    this.itemsPaginated = [];
    this.pagination = {
      total: 0,
      page: 1,
      limit: 10,
      pages: 0,
      hasNextPage: false,
      hasPrevPage: false,
    };

    makeObservable(this, {
      selected: observable,
      filters: observable,
      items: observable,
      pagination: observable,
      filteredItems: computed,
      setSelected: action,
      setFilters: action,
      setResetFilter: action,
      setPagination: action,
      
      fetch: flow,
      fetchPaginate: flow,
      fetchOne: flow,

      create: flow,
      createMany: flow,
      update: flow,
      delete: flow,
    });
    makePersistable(this, {
      name: 'Room', properties: ['selected', 'items', 'filters'],
      storage: typeof window !== "undefined" ? window.localStorage : undefined,
      // expireIn: 604800000, // 7 day in milliseconds to match refresh token expiry time
      // removeOnExpiration: true,
    });
  }

  get filteredItems() {
    let filteredItems = this.items;
    return filteredItems;
  }

  setSelected = (room) => (this.selected = room);

  setPagination = (pagination) => (this.pagination = pagination);

  setFilters = (filters = {}) => {
    const applyingFilter = {...this.filters, ...filters};
    // this.fetch(applyingFilter);
    this.fetchPaginate(applyingFilter);
    return this.filters = applyingFilter;
  }

  setResetFilter = () => {
    this.setFilters(defaultFilter);
  };

  *fetch(params = {}) {
    try {
      const plainParams = toJS(params); // convert to plain object
      const queryString = Object.keys(plainParams)
        .filter(key => plainParams[key] !== undefined && plainParams[key] !== '' && plainParams[key] !== null)
        .map(key => {
          const value = plainParams[key];
          // If value is an array, join it with comma and encode
          if (Array.isArray(value) && value.length > 0) {
            return `${key}=${encodeURIComponent(value.join(','))}`;
          }
          // For non-array values
          return `${key}=${encodeURIComponent(value)}`;
        })
        .join('&');

      const response = yield apiFetcher().get(`/rooms/with-paginate?${queryString}`);
      this.items = response?.data?.data || response?.data;

      // Store pagination info from backend
      if (response.data.pagination) {
        this.setPagination(response.data.pagination);
      }

      if (this.selected?._id) {
        this.setSelected(
          this.items.find((item) => item._id === this.selected._id) || {}
        );
      }
      return { status: 200, data: response.data };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *fetchPaginate(params = {}) {
    try {
      const plainParams = toJS(params); // convert to plain object
      const queryString = Object.keys(plainParams)
        .filter(key => plainParams[key] !== undefined && plainParams[key] !== '' && plainParams[key] !== null)
        .map(key => {
          const value = plainParams[key];
          // If value is an array, join it with comma and encode
          if (Array.isArray(value) && value.length > 0) {
            return `${key}=${encodeURIComponent(value.join(','))}`;
          }
          // For non-array values
          return `${key}=${encodeURIComponent(value)}`;
        })
        .join('&');

      const response = yield apiFetcher().get(`/rooms/with-paginate?${queryString}`);
      this.itemsPaginated = response?.data?.data || response?.data;

      // Store pagination info from backend
      if (response.data.pagination) {
        this.setPagination(response.data.pagination);
      }

      if (this.selected?._id) {
        this.setSelected(
          this.itemsPaginated.find((item) => item._id === this.selected._id) || {}
        );
      }

      return { status: 200, data: response.data };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *fetchOne(roomId) {
    try {
      const response = yield apiFetcher().get(`/rooms/${roomId}`);
      const updatedRoom = response.data;
      this.items = updateItems(updatedRoom, this.items);
      if (this.selected?._id === updatedRoom._id) {
        this.selected = updatedRoom;
      }
      return { status: 200, data: response.data };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *create(room) {
    try {
      const response = yield apiFetcher().post('/rooms', room);
      const createdRoom = response.data;
      this.items = updateItems(createdRoom, this.items);

      return { status: 200, data: createdRoom };
    } catch (error) {
      return { status: error?.response?.status, error_code: error?.response?.data?.error_code };
    }
  }

  *createMany(rooms) {
    try {
      const response = yield apiFetcher().post('/rooms/create_many', rooms);
      const createdRoom = response.data;
      this.items = updateItems(createdRoom, this.items);

      return { status: 200, data: createdRoom }
    } catch (error) {
      return { status: error?.response?.status, error_code: error?.response?.data?.error_code };
    }
  }

  *update(room) {
    try {
      const response = yield apiFetcher().patch(
        `/rooms/${room._id}`,
        room
      );
      const updatedRoom = response.data;
      this.items = updateItems(updatedRoom, this.items);
      if (this.selected?._id === updatedRoom._id) {
        this.setSelected(updatedRoom);
      }
      return { status: 200, data: updatedRoom };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }

  *delete(ids) {
    try {
      yield apiFetcher().delete(`/rooms/${ids.join(',')}`);
      return { status: 200 };
    } catch (error) {
      return { status: error?.response?.status };
    }
  }
}
